// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import actionsSims from '../../../api/actionsSims';
import actionsOrganizations from '@/app/modules/organizations/api/actionsOrganizations';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import OrganizationsTable from '@/app/modules/organizations/components/tables/OrganizationsTable';
import AddSimCardsByProvider from './components/AddSimCardsByProvider';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import ORGANIZATION_TYPES from '@/app/modules/organizations/enums/OrganizationTypes';
import SIM_OFFERS_PROVIDERS from '../../../enums/SimOffersProviders';
import splitLines from '../../../funcs/splitLines';
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import organizationFields from '@/app/modules/organizations/fields/organizationFields';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsOrganization, nsSimCard } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 * @returns {JSX.Element} ModalResetPassword
 */
function ModalAddSimCardToClient({ isOpen, setIsOpen, setLaunchRefetch }) {

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.add.steps';

    // Toast
    const { toast } = useToast();

    // State
    const [step, setStep] = useState(0);
    const [simProvider, setSimProvider] = useState('');
    const [simNumberList, setSimNumberList] = useState('');
    const [clientSelected, setClientSelected] = useState(getInitialValueForRowSelected());
    const [simProvidersOptions, setSimProvidersOptions] = useState(null);
    const [simsValidationOnError, setSimsValidationOnError] = useState(true);

    // API Calls
    // Queries
    const { data, isLoading, isError, refetch } = actionsOrganizations.getOrganizationSimOffers({ organization: clientSelected?.rowsData[0]?.uuid }, false);
    // Mutations
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity={'modal.add.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.add.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitAddSimCards = actionsSims.addSimCards(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.client_selection`, disabledNext: !clientSelected.rowsData.length },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.add_sims`, disabledNext: (simProvidersOptions && !simProvider) || simsValidationOnError }
        }),
        [clientSelected, simProvidersOptions, simProvider, simsValidationOnError]
    );

    useEffect(() => {
        if (step === 0) {
            // State reset
            setSimProvidersOptions(null);
            setSimProvider('');
            setSimNumberList('');
        }
        if (step === 1) {
            refetch();
        }
    }, [step]);

    useEffect(() => {
        if (data && Object.keys(data).length > 0) {
            setSimProvidersOptions(Object.keys(data).map(key => {
                const provider = Object.values(SIM_OFFERS_PROVIDERS).find(elem => elem.name === key);
                return {
                    name: provider.entities.name,
                    value: provider.key,
                    namespace: nsOrganization
                };
            }));
        }
    }, [data]);

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn(step !== 0 ? 'max-w-lg' : 'max-w-6xl')}>
                <HeaderTitle title={'modal.add.title'} namespace={nsSimCard} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {
                        step === 0 &&
                        <OrganizationsTable
                            filtersEventName={setFiltersEventName('organizations', true)}
                            customQuery={{
                                filters: [
                                    {
                                        key: organizationFields.TYPE.key,
                                        value: ORGANIZATION_TYPES.WHITE_LABEL.name,
                                        hidden: true
                                    }
                                ]
                            }}
                            singleSelection={true}
                            rowsSelected={clientSelected}
                            setRowsSelected={setClientSelected}
                        />
                    }
                    {
                        step === 1 && (
                            <>
                                {isLoading &&
                                    <Spinner className='mt-8' />
                                }
                                {!isLoading &&
                                    <>
                                        {isError ?
                                            <Entity entity={'modal.add.error_providers'} namespace={nsSimCard} />
                                            :
                                            <AddSimCardsByProvider
                                                simProvider={simProvider}
                                                setSimProvider={setSimProvider}
                                                simNumberList={simNumberList}
                                                setSimNumberList={setSimNumberList}
                                                setDisableSubmit={setSimsValidationOnError}
                                                providersOptions={simProvidersOptions}
                                            />
                                        }
                                    </>
                                }
                            </>
                        )
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    stepsNumber={STEPS_NUMBER}
                    setStep={setStep}
                    onValidate={() => {
                        submitAddSimCards.mutate({
                            simProvider: simProvider,
                            iccids: splitLines(simNumberList).filter((simNumber, index) => index <= 249).join('\n'),
                            organizationId: clientSelected?.rowsData[0]?.uuid
                        });
                        if (splitLines(simNumberList).length > 249) {
                            submitAddSimCards.mutate({
                                simProvider: simProvider,
                                iccids: splitLines(simNumberList).filter((simNumber, index) => index > 249).join('\n'),
                                organizationId: clientSelected?.rowsData[0]?.uuid
                            });
                        }
                    }}
                    disableValidation={disabledNext}
                />
            </ModalContent>
        </Modal>
    );
}

ModalAddSimCardToClient.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default ModalAddSimCardToClient;